import api, { ListResponse } from "features/api";

export type TicketType = "Bug" | "Suggestion";
export type TicketStatus = "New" | "In Progress" | "Done" | "Error";
export type ScreenshotContentType = "image/png" | "image/jpeg" | "image/webp";

export interface TicketSummary {
  id: number;
  type: TicketType;
  status: TicketStatus;
  description: string;
  creator_email: string;
  created_on: string;
  /** Null until the ticket is first changed after it was filed. */
  updated_on: string | null;
  screenshot_count: number;
}

export interface TicketScreenshot {
  id: number;
  filename: string;
  /** Usable directly as an `<img src>`: a presigned S3 URL or a data: URI. */
  url: string;
}

export interface Ticket extends TicketSummary {
  /** Set exactly when `status` is "Error": why processing the ticket failed. */
  error_message: string | null;
  operating_system: string;
  browser: string;
  screenshots: TicketScreenshot[];
}

export interface NewTicketScreenshot {
  filename: string;
  content_type: ScreenshotContentType;
  /** Base64 WITHOUT a `data:` prefix: the backend decodes it as-is. */
  data: string;
}

export interface CreateTicketParams {
  type: TicketType;
  description: string;
  creator_email: string;
  operating_system: string;
  browser: string;
  screenshots: NewTicketScreenshot[];
}

// The backend's limits, restated so the form can refuse first.
export const TICKET_DESCRIPTION_MAX_LENGTH = 2000;
export const TICKET_MAX_SCREENSHOTS = 3;
/** Per screenshot, measured on the decoded bytes. */
export const TICKET_SCREENSHOT_MAX_BYTES = 1048576;
export const TICKET_PAGE_SIZE = 10;

export const TICKET_DONE_DISCLAIMER =
  "The ticket has been closed but the changes will be deployed manually by the development team shortly.";

const LIST_TAG = { type: "Ticket" as const, id: "LIST" };

export const apiWithTickets = api.injectEndpoints({
  endpoints: (builder) => ({
    listTickets: builder.query<ListResponse<TicketSummary>, number>({
      query: (page) => ({ url: `/tickets/`, params: { page } }),
      providesTags: (result) => [
        LIST_TAG,
        ...(result?.results ?? []).map(({ id }) => ({
          type: "Ticket" as const,
          id,
        })),
      ],
    }),

    getTicket: builder.query<Ticket, number>({
      query: (id) => `/tickets/${id}/`,
      providesTags: (_result, _error, id) => [{ type: "Ticket", id }],
    }),

    createTicket: builder.mutation<Ticket, CreateTicketParams>({
      query: (body) => ({ url: `/tickets/`, method: "POST", body }),
      invalidatesTags: [LIST_TAG],
    }),
  }),
});

export const {
  useListTicketsQuery,
  useGetTicketQuery,
  useCreateTicketMutation,
} = apiWithTickets;
